<template>
  <section
    data-cy="marketing-edit-advert-view"
    :data-view-ready="!loading">
    <div>
      <!-- Heading -->
      <div class="row justify-content-between align-items-center">
        <div
          v-if="object"
          class="col-auto">
          <title-marketing-object-view
            :show-marketing-info-box="false"
            :object="object" />
        </div>
        <div
          v-else
          class="col-auto">
          <h1>
            {{ $t('message.managementOverview.objects') }}
          </h1>
        </div>
      </div>
      <div class="row">
        <div class="col-12 marketing-edit-advert">
          <form-wizard
            id="wizard"
            ref="wizard"
            data-cy="marketing-edit-advert-wizard"
            title=""
            subtitle=""
            step-size="xs"
            color="rgb(0, 68, 95)"
            @on-complete="publishData">
            <template #title>
              <span />
            </template>
            <tab-content
              :title="$t('message.generic.basics')"
              :before-change="validateTabOne">
              <div
                v-if="loading"
                class="col-12 text-center">
                <coozzy-spinner class="m-3" />
              </div>
              <div v-else>
                <coozzy-card
                  class="global-card mb-3">
                  <h5 class="cursor-pointer">
                    {{ $t('message.manageBuilding.building') }}
                  </h5>
                  <object-basic
                    v-if="object"
                    ref="object-basics"
                    :key="`basic-${object.id}`"
                    :object-selected="object"
                    :expand-by-default="true"
                    :edit-advert-call="true"
                    @edit-building="editBuildingFields" />
                </coozzy-card>
                <coozzy-card
                  class="global-card mb-3">
                  <characteristics
                    v-if="building"
                    ref="characteristics"
                    :building="building"
                    :title="$t('message.onBoarding.createBuilding.features.marketBase')"
                    :expand-by-default="true"
                    @value-updated="buildingCharacteristicsUpdated" />
                </coozzy-card>
                <coozzy-card
                  class="global-card mb-3">
                  <object-features
                    v-if="object"
                    ref="object-features"
                    :key="`features-${object.id}`"
                    :title="$t('message.onBoarding.buildings.objects.features.title.marketBase')"
                    :object-selected="object"
                    :expand-by-default="true" />
                </coozzy-card>
                <coozzy-card class="global-card mb-3">
                  <building-distances
                    v-if="building"
                    ref="building-distances"
                    :building="building"
                    :expand-by-default="true"
                    @value-updated="buildingDistanceUpdated" />
                </coozzy-card>
              </div>
            </tab-content>
            <tab-content
              :title="$t('message.marketingObjects.editAdvert.tabs.advert')"
              :before-change="validateTabTwo">
              <div
                v-if="loading"
                class="col-12 text-center">
                <coozzy-spinner class="m-3" />
              </div>
              <div v-else>
                <coozzy-card
                  class="global-card mb-3 readonly-wrapper">
                  <object-pricing
                    v-if="object"
                    ref="object-pricing"
                    :key="`pricing-${object.id}`"
                    :object-selected="object"
                    :expand-by-default="false"
                    :read-only="true" />
                </coozzy-card>
                <coozzy-card
                  class="global-card mb-3 readonly-wrapper">
                  <div
                    id="Tenant"
                    v-b-toggle.coozzy-card-tenancy
                    class="collapse-title">
                    <h5 class="cursor-pointer">
                      {{ $t('message.onBoarding.buildings.objects.tenancies.title') }} ({{ currentTenancies.length }})
                    </h5>
                  </div>
                  <div class="col-12 p-0">
                    <b-collapse
                      id="coozzy-card-tenancy"
                      :visible="false">
                      <div
                        v-if="currentTenancies.length === 0"
                        class="row mt-3">
                        <div class="col-12">
                          <h6>
                            {{ $t('message.nonExistent.tenancies') }}
                          </h6>
                        </div>
                      </div>
                      <template
                        v-for="lastTenancy of currentTenancies"
                        v-else>
                        <object-tenancy
                          v-if="!lastTenancy.vacancy"
                          :key="'ten-'+lastTenancy.id"
                          ref="object-tenancy"
                          :tenancy="lastTenancy"
                          :object="object"
                          :read-only="true"
                          :expand-by-default="false"
                          :hide-actions="true"
                          :show-digital-deposit-only="true" />
                        <object-vacancy
                          v-else
                          :key="'vac-'+lastTenancy.id"
                          ref="object-vacancy"
                          :tenancy="lastTenancy"
                          :object="object"
                          :read-only="true"
                          :expand-by-default="false" />
                      </template>
                    </b-collapse>
                  </div>
                </coozzy-card>
                <coozzy-card class="global-card mb-3">
                  <object-advertising
                    v-if="object"
                    ref="object-advertising"
                    :key="`advertising-${object.id}`"
                    :object-selected="object"
                    :property="property"
                    :expand-by-default="true"
                    :saving-before-ai-advert="savingBeforeAiAdvert"
                    @update-object-advertising="updateObjectAdvertising"
                    @ai-advert-applied="onAiAdvertApplied"
                    @request-ai-advert-generation="openAdvertGenerationModal" />
                </coozzy-card>
                <coozzy-card class="global-card mb-3">
                  <object-possible-deposits
                    v-if="object"
                    ref="object-possible-deposits"
                    :key="`possible-deposits-${object.id}`"
                    :object="object"
                    :expand-by-default="true" />
                </coozzy-card>
                <coozzy-card class="global-card mb-3">
                  <template v-if="object">
                    <h5 class="cursor-pointer">
                      {{ $t('message.export.creditTrust.title') }}
                    </h5>
                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <span v-html="$t('message.export.creditTrust.description')" />
                    <coozzy-form-checkbox
                      id="acceptsCreditTrust"
                      :key="'acceptsCreditTrust'"
                      v-model="acceptsCreditTrust"
                      data-cy="marketing-edit-advert-credit-trust-checkbox"
                      :initial="acceptsCreditTrust"
                      class="align-self-center mt-3">
                      {{ $t('message.export.creditTrust.acceptCertificate') }}
                    </coozzy-form-checkbox>
                  </template>
                </coozzy-card>
                <automatic-reference-check-card
                  v-if="object"
                  ref="automaticReferenceCheckCard"
                  v-model="automaticReferenceCheckQueryMode"
                  :sender-mailbox-id.sync="automaticReferenceCheckSenderMailboxId" />
              </div>
            </tab-content>
            <tab-content
              :title="$t('message.marketingObjects.editAdvert.tabs.communication')"
              :before-change="validateTabThree">
              <div
                v-if="loading"
                class="col-12 text-center">
                <coozzy-spinner class="m-3" />
              </div>
              <marketing-edit-advert-communication-tab
                v-else
                ref="editAdvertCommunicationTab"
                :object="object"
                :building="building"
                @default-answer-updated="defaultAnswerUpdated"
                @viewing-appointment-updated="viewingAppointmentUpdated"
                @viewing-contact-updated="viewingContactUpdated"
                @assignee-marketing-updated="assigneeMarketingUpdated"
                @publish-assignee-phone-updated="publishAssigneePhoneUpdated"
                @empty-default-answer="emptyDefaultAnswer" />
            </tab-content>
            <tab-content
              :title="$t('message.marketingObjects.editAdvert.tabs.export')"
              :before-change="validateTabFour">
              <div
                v-if="loading"
                class="col-12 text-center">
                <coozzy-spinner class="m-3" />
              </div>
              <coozzy-card
                v-if="!loading"
                class="global-card mb-3">
                <h5 class="cursor-pointer">
                  {{ $t('message.export.title') }}
                </h5>
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="$t('message.export.advertPlatformInfo')" />
                <export-platforms
                  v-if="object"
                  ref="export-platforms"
                  :show-save-button="false"
                  :advert-ids="[object.id]"
                  :source="'editAdvert'"
                  @platforms-changed="platformsChanged" />
                <div
                  v-if="newhomeConfigured"
                  class="newhome-config mt-4 mb-4">
                  <h5 class="cursor-pointer mb-1 mt-3">
                    {{ $t('message.export.newhomeConfigurationTitle') }}
                  </h5>
                  <a
                    :href="$t('message.export.newhomePromotionLink')"
                    target="_blank"
                    rel="noopener noreferrer">
                    {{ $t('message.export.newhomePromotionSubtitle') }}
                  </a>
                  <div
                    class="newhome-config-content mt-3"
                    :class="{ 'is-disabled': !isNewhomeConfigurationEditable }">
                    <div class="row align-items-end">
                      <div class="col-6">
                        <label class="mb-2">{{ $t('message.export.newhomePromotionType') }}</label>
                        <div class="segmented-buttons">
                          <button
                            v-for="option in ['top', 'highlight', 'special']"
                            :id="`promotion-${option}`"
                            :key="option"
                            type="button"
                            class="btn segmented-button mr-2"
                            :class="{ active: promotionType === option }"
                            :disabled="!isNewhomeConfigurationEditable"
                            @click="setPromotionType(option)">
                            {{ $t(`message.export.newhomePromotionOptions.${option}`) }}
                          </button>
                        </div>
                      </div>
                      <div class="col-3">
                        <label class="mb-0">
                          {{ $t('message.export.newhomePromotionPeriod') }}
                          <coozzy-info-circle-icon
                            id="promotionPeriodInfo"
                            v-b-tooltip.hover
                            class="ml-1"
                            :title="$t('message.export.newhomePromotionPeriodTooltip')" />
                        </label>
                        <coozzy-form-select
                          id="promotionPeriod"
                          v-model="promotionPeriod"
                          :disabled="!isNewhomeConfigurationEditable || !promotionType">
                          <option :value="null">
                            -
                          </option>
                          <option :value="15">
                            {{ $t('message.export.newhomePromotionPeriodOptions.days15') }}
                          </option>
                          <option :value="30">
                            {{ $t('message.export.newhomePromotionPeriodOptions.days30') }}
                          </option>
                          <option :value="60">
                            {{ $t('message.export.newhomePromotionPeriodOptions.days60') }}
                          </option>
                        </coozzy-form-select>
                      </div>
                      <div class="col-3">
                        <coozzy-form-input
                          id="promotionStartDate"
                          :name="$t('message.export.newhomePromotionStartDate')"
                          type="date"
                          :value="promotionStartDate"
                          :state="$v.promotionStartDate && $v.promotionStartDate.$dirty && $v.promotionStartDate.$error ? false : null"
                          :min="getTodayDateInput()"
                          :disabled="!isNewhomeConfigurationEditable || !promotionType"
                          @input="promotionStartDateChanged" />
                      </div>
                    </div>
                    <div class="row mt-3 align-items-end">
                      <div class="col-6">
                        <coozzy-form-checkbox
                          id="newhomeFirst"
                          :initial="newhomeFirst"
                          :disabled="!isNewhomeConfigurationEditable"
                          @change="(value) => { newhomeFirst = value }">
                          {{ $t('message.export.newhomeFirst') }}
                          <coozzy-info-circle-icon
                            id="newhomeFirstInfo"
                            v-b-tooltip.hover
                            class="ml-1"
                            :title="$t('message.export.newhomeFirstTooltip')" />
                        </coozzy-form-checkbox>
                      </div>
                      <div
                        class="col-6"
                        :style="newhomeFirst ? '' : 'visibility: hidden'">
                        <coozzy-form-input-numeric
                          id="newhomeFirstDays"
                          :name="$t('message.export.newhomeFirstDays') + ' *'"
                          :value="newhomeFirstDays"
                          :is-integer="true"
                          :is-integer-format="true"
                          :disabled="!isNewhomeConfigurationEditable || !newhomeFirst"
                          :check-valide="newhomeFirst && attemptedSaveTabFour && $v.newhomeFirstDays && $v.newhomeFirstDays.$dirty && $v.newhomeFirstDays.$error ? 'is-invalid' : ''"
                          @input="(value) => { newhomeFirstDays = value }" />
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-2">
                    <label class="mb-0 mt-3">{{ $t('message.advertisement.onlineFrom') }}</label>
                    <coozzy-form-select
                      id="onlineFrom"
                      v-model="onlineFrom">
                      <option value="immediately">
                        {{ $t('message.advertisement.immediately') }}
                      </option>
                      <option value="date">
                        {{ $t('message.tableColumnHeadings.date') }}
                      </option>
                    </coozzy-form-select>
                  </div>
                  <div
                    v-if="onlineFrom === 'date'"
                    class="col-2 mt-3">
                    <coozzy-form-input
                      id="onlineFromDate"
                      v-model="onlineFromDate"
                      type="date"
                      :state="$v.onlineFromDate && $v.onlineFromDate.$dirty && $v.onlineFromDate.$error ? false : null"
                      :name="$t('message.tableColumnHeadings.date')" />
                  </div>
                  <div
                    v-if="onlineFrom === 'date'"
                    class="col-2">
                    <label
                      for="time"
                      class="mb-0 mt-3">
                      {{ $t('message.advertisement.time') }}
                    </label>
                    <coozzy-form-select
                      id="timeFrom"
                      v-model="time.start">
                      <option value="09:30">
                        09:30
                      </option>
                      <option value="12:30">
                        12:30
                      </option>
                      <option value="17:30">
                        17:30
                      </option>
                    </coozzy-form-select>
                  </div>
                  <div class="col-2">
                    <label class="mb-0 mt-3">{{ $t('message.advertisement.onlineUntil') }}</label>
                    <coozzy-form-select
                      id="onlineUntil"
                      v-model="onlineUntil">
                      <option value="">
                        -
                      </option>
                      <option value="date">
                        {{ $t('message.tableColumnHeadings.date') }}
                      </option>
                      <option value="amount">
                        {{ $t('message.advertisement.amountOfRequests') }}
                      </option>
                    </coozzy-form-select>
                  </div>
                  <div
                    v-if="onlineUntil === 'date'"
                    class="col-2 mt-3">
                    <coozzy-form-input
                      id="onlineUntil"
                      ref="onlineUntil"
                      v-model="unPublishAt"
                      type="date"
                      :state="$v.unPublishAt && $v.unPublishAt.$dirty && $v.unPublishAt.$error ? false : null"
                      :name="$t('message.tableColumnHeadings.date')" />
                  </div>
                  <div
                    v-if="onlineUntil === 'date'"
                    class="col-2">
                    <label
                      for="time"
                      class="mb-0 mt-3">
                      {{ $t('message.advertisement.time') }}
                    </label>
                    <coozzy-form-select
                      id="timeUntil"
                      v-model="time.end">
                      <option value="09:30">
                        09:30
                      </option>
                      <option value="12:30">
                        12:30
                      </option>
                      <option value="17:30">
                        17:30
                      </option>
                    </coozzy-form-select>
                  </div>
                  <div
                    v-if="onlineUntil === 'amount'"
                    class="col-2">
                    <label
                      for="time"
                      class="mb-0 mt-3"
                      style="opacity: 0;">
                      date
                    </label>
                    <coozzy-form-input-numeric
                      v-model="unPublishAtAmountOfRequests"
                      type="number"
                      :is-integer="true"
                      :is-integer-format="true"
                      min="0"
                      :check-valide="$v.unPublishAtAmountOfRequests.$dirty && $v.unPublishAtAmountOfRequests.$error ? 'is-invalid' : ''" />
                  </div>
                </div>
                <label
                  for="language"
                  class="mb-0 mt-3">
                  {{ $t('message.advertisement.labelLanguage') }}
                </label>
                <coozzy-form-select
                  v-if="object"
                  id="language"
                  v-model="marketingLanguage"
                  class="w-50">
                  <option value="PREFERRED_LANGUAGE_GERMAN">
                    {{ $t('message.language.german') }}
                  </option>
                  <option value="PREFERRED_LANGUAGE_ENGLISH">
                    {{ $t('message.language.english') }}
                  </option>
                  <option value="PREFERRED_LANGUAGE_ITALIAN">
                    {{ $t('message.language.italian') }}
                  </option>
                </coozzy-form-select>
              </coozzy-card>
            </tab-content>
            <template
              #footer="props">
              <div
                v-if="!loading"
                class="wizard-footer-left">
                <wizard-button
                  v-if="props.activeTabIndex === 0"
                  :style="props.fillButtonStyle"
                  @click.native="clickBack()">
                  <!-- clickBack() -->
                  {{ $t('message.marketingObjects.editAdvert.cancel') }}
                </wizard-button>
                <wizard-button
                  v-else
                  :style="props.fillButtonStyle"
                  @click.native="backClicked(props)">
                  {{ $t('message.marketingObjects.editAdvert.back') }}
                </wizard-button>
              </div>
              <div
                v-if="!loading"
                class="wizard-footer-right">
                <wizard-button
                  v-if="!props.isLastStep"
                  id="publish-btn"
                  data-cy="marketing-edit-advert-next-button"
                  class="wizard-footer-right"
                  :style="!props.isLastStep || canBePublished ? props.fillButtonStyle : 'color:white;border-color: grey !important;background: grey !important;cursor: default;'"
                  @click.native="nextClicked(props, canBePublished)">
                  {{ $t('message.marketingObjects.editAdvert.next') }}
                </wizard-button>
                <wizard-button
                  v-if="props.isLastStep"
                  id="publish-btn"
                  data-cy="marketing-edit-advert-publish-button"
                  class="wizard-footer-right mr-2"
                  :class="isRented ? 'cursor-normal' : ''"
                  :style="props.isLastStep && !canBePublished ? 'color:white;border-color: grey !important;background: grey !important;cursor: default;' : props.fillButtonStyle"
                  :disabled="isRented"
                  @click.native="canBePublished ? save(props.activeTabIndex, true) : null">
                  {{ $t('message.marketingObjects.editAdvert.publish') }}
                </wizard-button>
                <wizard-button
                  v-if="!props.isLastStep"
                  id="save-btn"
                  data-cy="marketing-edit-advert-save-draft-button"
                  class="wizard-footer-right mr-2"
                  :style="props.fillButtonStyle"
                  @click.native="save(props.activeTabIndex, false)">
                  {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
                </wizard-button>
                <wizard-button
                  v-if="props.isLastStep"
                  id="expose-btn"
                  data-cy="marketing-edit-advert-save-open-expose-button"
                  class="wizard-footer-right mr-2"
                  :style="props.fillButtonStyle"
                  @click.native="$bvModal.show('openExposeModal')">
                  {{ $t('message.advertisement.action.saveAndOpenExpose') }}
                </wizard-button>
                <b-tooltip
                  v-if="!canBePublished && props.isLastStep"
                  target="publish-btn"
                  triggers="hover">
                  {{ $t(publishTooltipKey) }}
                </b-tooltip>
              </div>
            </template>
          </form-wizard>
        </div>
      </div>
    </div>
    <b-modal
      ref="modal-confirmation"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')">
      <div class="row mb-2">
        <div class="col">
          <p class="mb-3">
            {{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}
          </p>
          <coozzy-button
            size="normal"
            design="green"
            @click="discardChanges">
            {{ $t('message.generic.discardChanges') }}
          </coozzy-button>
          <coozzy-button
            size="normal"
            class="float-right mr-3 border"
            design="transparent"
            @click="closeModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </div>
    </b-modal>
    <b-modal
      :id="'openExposeModal'"
      ref="openExposeModal"
      data-cy="marketing-edit-advert-open-expose-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.advertisement.action.openExpose')">
      <template>
        <div class="col-12">
          <p>{{ $t('message.advertisement.action.titleModal') }}</p>
        </div>
        <div class="col-12 mt-3 mb-3">
          <coozzy-form-select
            v-model="language"
            data-cy="marketing-edit-advert-open-expose-language-select">
            <option
              v-for="option in optionsLanguages"
              :key="option.key"
              :value="option.key">
              {{ option.value }}
            </option>
          </coozzy-form-select>
        </div>
        <div class="col">
          <coozzy-button
            size="small"
            class="mb-0 border"
            design="transparent"
            data-cy="marketing-edit-advert-open-expose-cancel-button"
            @click="$bvModal.hide('openExposeModal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            class="float-right mb-0"
            design="green"
            data-cy="marketing-edit-advert-open-expose-confirm-button"
            @click="openAdvertExpose">
            {{ $t('message.generic.confirm') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
  </section>
</template>

<script>
import ObjectFeatures from '@/building/tabs/components/ObjectFeatures'
import ObjectBasic from '@/building/tabs/components/ObjectBasic'
import ObjectPricing from '@/building/tabs/components/ObjectPricing'
import ObjectTenancy from '@/building/tabs/components/ObjectTenancy'
import ObjectVacancy from '@/building/tabs/components/ObjectVacancy'
import ObjectAdvertising from '@/building/tabs/components/ObjectAdvertising'
import ObjectPossibleDeposits from '@/building/tabs/components/ObjectPossibleDeposits'
import Characteristics from '@/building/tabs/components/Characteristics'
import BuildingDistances from '@/building/tabs/components/BuildingDistances'
import TitleMarketingObjectView from '@/marketing/TitleMarketingObjectView'
import { user } from '@/mixins/user'
import { formatting } from '@/mixins/formatting'
import { obj } from '@/mixins/object'
import { html } from '@/mixins/html'
import ObjectApi from '../misc/apis/ObjectApi'
import AdvertGenerationApi from '@/misc/apis/AdvertGenerationApi'
import { AiadvertGenerationFeedbackRating } from '@/types/api'
import RequestApi from '@/marketingBasic/api/RequestApi'
import Vue from 'vue'
import { minValue, required } from 'vuelidate/lib/validators'
import { mapGetters, mapMutations } from 'vuex'
import CoozzySpinner from '../framework/components/misc/CoozzySpinner'
import CoozzyCard from '../framework/components/card/CoozzyCard'
import ExportPlatforms from '@/properties/components/ExportPlatforms'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import { algolia } from '@/mixins/algolia'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import MarketingEditAdvertCommunicationTab from '@/marketing/MarketingEditAdvertCommunicationTab'
import CoozzyFormCheckbox from '@/framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon'
import AutomaticReferenceCheckCard from '@/marketing/components/AutomaticReferenceCheckCard.vue'
import { REFERENCE_CHECK_QUERY_MODES } from '@/types/models'
import {
  fromApiQueryMode,
  isUndefinedAutomaticReferenceCheck,
  isUndefinedReferenceSenderMailboxId,
  loadReferenceCheckSettings,
  toObjectApiAutomaticReferenceCheck,
  toObjectApiReferenceSenderMailboxId
} from '@/settings/marketing/referenceCheckSettings'

function moreThanOverall(value, vm) {
  return value > vm.overall
}
function moreThanToday(value, vm) {
  return new Date(value + ' ' + vm.time.end + ':00') >= new Date()
}
function moreThanTodayOnlineFrom(value, vm) {
  return new Date(value + ' ' + vm.time.start + ':00') >= new Date()
}
function todayOrFuture(value) {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return new Date(value + 'T00:00:00') >= today
}
function onlineUntilAfterOnlineFrom(value, vm) {
  if (vm.onlineFrom !== 'date' || !vm.onlineFromDate || !value) {
    return true
  }
  return new Date(value + ' ' + vm.time.end + ':00') >= new Date(vm.onlineFromDate + ' ' + vm.time.start + ':00')
}
function promotionStartAtOrAfterOnlineFrom(value, vm) {
  if (vm.onlineFrom !== 'date' || !vm.onlineFromDate || !value) {
    return true
  }
  return new Date(value + 'T00:00:00') >= new Date(vm.onlineFromDate + 'T00:00:00')
}
function promotionStartAtOrBeforeOnlineUntil(value, vm) {
  if (vm.onlineUntil !== 'date' || !vm.unPublishAt || !value) {
    return true
  }
  return new Date(value + 'T00:00:00') <= new Date(vm.unPublishAt + 'T00:00:00')
}
function promotionPeriodWithinOnlineUntil(value, vm) {
  if (vm.onlineUntil !== 'date' || !vm.unPublishAt || !vm.promotionStartDate || !value) {
    return true
  }
  const promotionEndDate = new Date(vm.promotionStartDate + 'T00:00:00')
  promotionEndDate.setDate(promotionEndDate.getDate() + Number(value) - 1)
  return promotionEndDate <= new Date(vm.unPublishAt + 'T00:00:00')
}

/** Maps app UI locale to persisted advert `marketingLanguage`; German when unknown. */
function marketingPreferredLanguageFromCurrentLocale(currentLanguage) {
  const lang = (currentLanguage || '').toString().toLowerCase()
  if (lang === 'en') {
    return 'PREFERRED_LANGUAGE_ENGLISH'
  }
  if (lang === 'it') {
    return 'PREFERRED_LANGUAGE_ITALIAN'
  }
  return 'PREFERRED_LANGUAGE_GERMAN'
}

export default {
  name: 'MarketingEditAdvertView',
  components: {
    AutomaticReferenceCheckCard,
    CoozzyInfoCircleIcon,
    CoozzyFormInput,
    CoozzyFormCheckbox,
    MarketingEditAdvertCommunicationTab,
    TitleMarketingObjectView,
    CoozzyButton,
    CoozzyFormSelect,
    ExportPlatforms,
    CoozzyCard,
    CoozzySpinner,
    Characteristics,
    ObjectFeatures,
    BuildingDistances,
    ObjectAdvertising,
    ObjectPossibleDeposits,
    ObjectBasic,
    ObjectPricing,
    ObjectTenancy,
    ObjectVacancy,
    CoozzyFormInputNumeric
  },
  mixins: [formatting, user, obj, algolia, html],
  props: {
    id: {
      type: String,
      default: null
    }
  },
  metaInfo() {
    return {
      title: `${this.$t('message.navigation.marketing.title')} ${this.buildingName}`
    }
  },
  data() {
    return {
      building: null,
      object: null,
      objectChanged: false,
      currentTenancies: [],
      hasPlatforms: false,
      loading: false,
      savingBeforeAiAdvert: false,
      language: 'DE',
      defaultAnswerNone: false,
      isRented: false,
      property: null,
      acceptsCreditTrust: null,
      automaticReferenceCheckInheritsFromAccount: true,
      accountReferenceCheckQueryMode: null,
      explicitAutomaticReferenceCheckQueryMode: REFERENCE_CHECK_QUERY_MODES.NONE,
      referenceSenderInheritsFromAccount: true,
      accountReferenceSenderMailboxId: null,
      explicitReferenceSenderMailboxId: '',
      unPublishAt: null,
      unPublishAtAmountOfRequests: null,
      time: {
        start: '17:30',
        end: '17:30'
      },
      onlineFrom: 'immediately',
      onlineFromDate: null,
      promotionType: '',
      promotionPeriod: null,
      promotionStartDate: null,
      promotionStartDateManuallyEdited: false,
      newhomeFirst: false,
      newhomeFirstDays: null,
      attemptedSaveTabFour: false,
      newhomeConfigured: false,
      newhomeSelected: false,
      onlineUntil: '',
      overall: 0,
      pendingAiGeneration: null
    }
  },
  computed: {
    buildingName() {
      if (this.building) {
        return this.building.name
      } else {
        return '-'
      }
    },
    onlineFromValue() {
      return this.onlineFrom
    },
    isNewhomeConfigurationEditable() {
      return this.newhomeConfigured && this.newhomeSelected
    },
    hasNewhomeConfigurationErrors() {
      if (!this.isNewhomeConfigurationEditable) {
        return false
      }
      if (this.promotionType && (!this.promotionPeriod || !this.promotionStartDate)) {
        return true
      }
      if (this.newhomeFirst && (!this.newhomeFirstDays || this.newhomeFirstDays < 1)) {
        return true
      }
      return false
    },
    onlineUntilValue() {
      return this.onlineUntil
    },
    optionsLanguages() {
      return [
        {
          key: 'EN',
          value: this.$t('message.language.english')
        },
        {
          key: 'DE',
          value: this.$t('message.language.german')
        },
        {
          key: 'IT',
          value: this.$t('message.language.italian')
        }]
    },
    getUserLang() {
      return this.$store.getters['user/getCurrentUser']['https://coozzy.ch/lang'].toUpperCase()
    },
    canBePublished() {
      return !!(this.object && this.object.title !== '' && this.object.description !== '' && this.object.category !== 'UNDEFINED_CATEGORY' &&
        this.object.pricing.currency !== '' && (this.object.pricing.coldRent !== '' || this.object.pricing.unit !== 'ON_REQUEST') && this.hasPlatforms &&
        this.onlineFrom !== '' && (this.onlineFrom !== 'date' || !!this.onlineFromDate) && !this.hasNewhomeConfigurationErrors)
    },
    automaticReferenceCheckQueryMode: {
      get() {
        if (this.automaticReferenceCheckInheritsFromAccount && this.accountReferenceCheckQueryMode) {
          return this.accountReferenceCheckQueryMode
        }
        return this.explicitAutomaticReferenceCheckQueryMode
      },
      set(value) {
        this.automaticReferenceCheckInheritsFromAccount = false
        this.explicitAutomaticReferenceCheckQueryMode = value
      }
    },
    automaticReferenceCheckSenderMailboxId: {
      get() {
        if (this.referenceSenderInheritsFromAccount && this.accountReferenceSenderMailboxId) {
          return this.accountReferenceSenderMailboxId
        }
        return this.explicitReferenceSenderMailboxId
      },
      set(value) {
        if (!value && this.referenceSenderInheritsFromAccount) {
          return
        }
        this.referenceSenderInheritsFromAccount = false
        this.explicitReferenceSenderMailboxId = value
      }
    },
    publishTooltipKey() {
      if (this.promotionType && !this.promotionPeriod) {
        return 'message.marketingObjects.noPromotionPeriodSelected'
      }
      if (this.newhomeFirst && (!this.newhomeFirstDays || this.newhomeFirstDays < 1)) {
        return 'message.savingErrors.validationError3'
      }
      return 'message.marketingObjects.impossibleToPublish'
    },
    applicationformLink() {
      return `${process.env.VUE_APP_BASE_API_URL}/object/${this.id}`
    },
    contactIdAssigneeMarketing: {
      get() {
        return this.object && this.object.contacts ? this.object.contacts.contactIdAssigneeMarketing : null
      },
      set(value) {
        if (this.object.contacts) {
          this.object.contacts.contactIdAssigneeMarketing = value
        } else {
          this.object.contacts = {
            contactIdAssigneeMarketing: value
          }
        }
      }
    },
    contactIdViewingAppointment: {
      get() {
        return this.object?.contacts?.contactIdViewingAppointment
      },
      set(newVal) {
        if (this.object.contacts) {
          this.object.contacts.contactIdViewingAppointment = newVal
        } else {
          this.object.contacts = {
            contactIdViewingAppointment: newVal
          }
        }
      }
    },
    marketingLanguage: {
      get() {
        const fromLocale = marketingPreferredLanguageFromCurrentLocale(this.currentLanguage)
        return this.object.marketingLanguage !== 'PREFERRED_LANGUAGE_UNDEFINED'
          ? this.object.marketingLanguage
          : fromLocale
      },
      set(value) {
        const obj = this.object
        obj.marketingLanguage = value
        this.$store.dispatch('onboarding/updateObject', obj)
      }
    },
    checkIfObjectOnLine() {
      // check if object is online
      if (this.object?.marketingStatus) {
         const pub = this.object?.marketingStatus.publications
        if (pub && pub.length > 0 && pub[pub.length - 1].unPublished) {
          if (parseInt(pub[pub.length - 1]?.unPublished) * 1000 > new Date().getTime() &&
            parseInt(pub[pub.length - 1]?.published) * 1000 <= new Date().getTime()) {
            // The object is currently published (online)
            return true
          } else {
            // The object is offline or scheduled to be published in the future
           return false
          }
        }
      }
      return false
    }
  },
  watch: {
    object: {
      deep: true,
      handler: function () {
        this.objectChanged = true
      }
    },
    onlineUntil: function (newVal) {
      if (newVal === 'date') {
        this.unPublishAtAmountOfRequests = null
      } else if (newVal === 'amount') {
        this.time.end = '17:30'
        this.unPublishAt = null
      } else {
        this.unPublishAtAmountOfRequests = null
        this.time.end = '17:30'
        this.unPublishAt = null
      }
    },
    onlineFrom: function (newVal) {
      if (newVal === 'date') {
        this.onlineFromDate = this.onlineFromDate || this.getTodayDateInput()
      } else {
        this.time.start = '17:30'
        this.onlineFromDate = null
      }
    },
    onlineFromDate: function (newVal) {
      const savedPromotedAt = this.object?.marketingStatus?.specialPublication?.promotedAt
      const hasSavedPromotionStartDate = !!(savedPromotedAt && savedPromotedAt !== '0')
      if (this.onlineFrom === 'date' && newVal && !this.promotionStartDateManuallyEdited && !hasSavedPromotionStartDate) {
        this.promotionStartDate = newVal
      }
    },
    newhomeFirst: function (newVal) {
      if (!newVal) {
        this.newhomeFirstDays = null
        this.attemptedSaveTabFour = false
      }
    },
    newhomeFirstDays: function (newVal) {
      if (newVal && Number(newVal) >= 1) {
        this.attemptedSaveTabFour = false
      }
    }
  },
  mounted: function () {
    this.$nextTick(function () {
      this.language = this.getUserLang || 'DE'
    })
    this.loadInstantSearchInternal()
    Promise.all([
      ObjectApi.getBuildingByObjectId(this.id),
      ObjectApi.getObject(this.id),
      ObjectApi.getTenancies([this.id]),
      RequestApi.getStatistics([this.id]),
      loadReferenceCheckSettings(this.accountId, this.currentLanguage)
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
          return null
        })
    ])
      .then(responses => {
        this.building = responses[0].building
        ObjectApi.getPropertiesByBuildingIds([this.building.id])
          .then(responseProp => {
            this.property = responseProp.properties[0]
          })
        this.object = responses[1].object
        this.acceptsCreditTrust = responses[1].object.acceptsCreditTrust
        this.automaticReferenceCheckInheritsFromAccount = isUndefinedAutomaticReferenceCheck(
          this.object.automaticReferenceCheck
        )
        this.accountReferenceCheckQueryMode = responses[4]?.queryMode ?? null
        this.accountReferenceSenderMailboxId = responses[4]?.senderMailboxId ?? null
        if (!this.automaticReferenceCheckInheritsFromAccount) {
          this.explicitAutomaticReferenceCheckQueryMode = fromApiQueryMode(this.object.automaticReferenceCheck)
        }
        this.referenceSenderInheritsFromAccount = isUndefinedReferenceSenderMailboxId(
          this.object.referenceSenderMailboxId
        )
        if (!this.referenceSenderInheritsFromAccount) {
          this.explicitReferenceSenderMailboxId = this.object.referenceSenderMailboxId || ''
        }
        const startOfDayTimestamp = this.getStartOfDayTimestamp()
        this.currentTenancies = responses[2].tenancies
          .sort((a, b) => new Date(this.$options.filters.objectToDateInput(a.startingAt)).getTime() / 1000 > new Date(this.$options.filters.objectToDateInput(b.startingAt)).getTime() / 1000 ? -1 : 1)
          .filter(t => t.startingAt && new Date(this.$options.filters.objectToDateInput(t.startingAt)).getTime() / 1000 <= startOfDayTimestamp && (!t.endingAt || (new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 < 0 || new Date(this.$options.filters.objectToDateInput(t.endingAt)).getTime() / 1000 >= startOfDayTimestamp)))
        this.overall = responses[3].statistics[0].overall
        this.loading = false
        this.$refs.wizard.activateAll()
        this.onlineFromDate = this.getTodayDateInput()
        this.promotionStartDate = this.getTodayDateInput()

        if (this.object.marketingLanguage === 'PREFERRED_LANGUAGE_UNDEFINED') {
          this.object.marketingLanguage = marketingPreferredLanguageFromCurrentLocale(this.currentLanguage)
        }
        const publications = this.object?.marketingStatus?.publications
        if (publications?.length > 0) {
          // Use the shared helper so only genuinely future publish/unpublish dates prefill the wizard.
          // A past unpublish timestamp (e.g. from a manual deactivate) must not auto-fill "Online until".
          const scheduled = this.getScheduledPublicationParams(this.object.marketingStatus)
          if (scheduled.publishAt) {
            const startTimeDate = new Date(scheduled.publishAt * 1000)
            const startHours = startTimeDate.getHours().toString().padStart(2, '0')
            const startMinutes = startTimeDate.getMinutes().toString().padStart(2, '0')
            this.onlineFrom = 'date'
            this.onlineFromDate = this.$options.filters.timestampToDateInput(scheduled.publishAt)
            this.time.start = `${startHours}:${startMinutes}`
          }
          if (this.checkIfObjectOnLine) {
            this.onlineFrom = 'date'
            this.onlineFromDate = this.getTodayDateInput()
          }
          if (scheduled.unPublishAt) {
            const timeEnd = new Date(scheduled.unPublishAt * 1000)
            const hoursEnd = timeEnd.getHours().toString().padStart(2, '0')
            const minutesEnd = timeEnd.getMinutes().toString().padStart(2, '0')
            this.time.end = hoursEnd + ':' + minutesEnd
            this.onlineUntil = 'date'
            this.unPublishAt = this.$options.filters.timestampToDateInput(scheduled.unPublishAt)
          } else if (scheduled.unPublishAtAmountOfRequests !== null && scheduled.unPublishAtAmountOfRequests !== undefined) {
            this.onlineUntil = 'amount'
            this.unPublishAtAmountOfRequests = scheduled.unPublishAtAmountOfRequests
          } else {
            this.onlineUntil = ''
          }
        }
        this.loadSpecialPublication()
        this.loadCurrentStatus()
        if (localStorage.getItem('oldCurrentStep') === '3') {
          this.$refs.wizard.changeTab(0, 3)
          localStorage.removeItem('oldCurrentStep')
        }
      })
      .catch(e => {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
      })
  },
  methods: {
    ...mapGetters('employee', ['getEmployees']),
    ...mapMutations('onboarding', ['setObjectOwnerContacts']),
    updateObjectAdvertising(obj) {
      this.object = obj
      this.invalidatePendingAiFeedbackIfNeeded()
    },
    onAiAdvertApplied({ generationId, title, description }) {
      this.pendingAiGeneration = generationId
        ? { generationId, title, description }
        : null
    },
    invalidatePendingAiFeedbackIfNeeded() {
      if (!this.pendingAiGeneration || !this.object) {
        return
      }
      if (
        this.object.title !== this.pendingAiGeneration.title ||
        this.object.description !== this.pendingAiGeneration.description
      ) {
        this.pendingAiGeneration = null
      }
    },
    async submitPendingPositiveAiFeedback() {
      const pending = this.pendingAiGeneration
      if (!pending?.generationId || !this.object) {
        return
      }
      if (
        this.object.title !== pending.title ||
        this.object.description !== pending.description
      ) {
        this.pendingAiGeneration = null
        return
      }
      this.pendingAiGeneration = null
      try {
        await AdvertGenerationApi.submitFeedback(
          pending.generationId,
          AiadvertGenerationFeedbackRating.FEEDBACK_RATING_POSITIVE
        )
      } catch (e) {
        console.error(e)
        Vue.toasted.show(this.$t('message.settings.ai.advertGeneration.feedbackFailed'), { type: 'error' })
      }
    },
    loadCurrentStatus() {
      if (this.object.marketingStatus && this.object.marketingStatus.clearFromMarketingAt > 0) {
        this.$nextTick(function () {
          this.isRented = true
        })
      }
    },
    async openAdvertExpose() {
      const isTabFourValid = await this.validateTabFour()
      if (isTabFourValid) {
        this.loading = true
        this.$bvModal.hide('openExposeModal')
        // save object and building before generate pdf
        await Promise.all([
          this.saveBuilding(),
          this.saveObject()
        ])
        const blob = new Blob([await ObjectApi.getMarketingExpose(this.object.id, this.language)], { type: 'application/pdf' })
        const blobURL = URL.createObjectURL(blob)
        window.open(blobURL)
        this.loading = false
        await this.$router.push({
          name: 'MarketingObjectDetailsView',
          params: { id: this.id },
          query: { view: 1 }
        })
      }
    },
    async openAdvertGenerationModal() {
      const isTabOneValid = await this.validateTabOne()
      if (!isTabOneValid) {
        Vue.toasted.show(this.$t('message.savingErrors.mandatoryMissing'), { type: 'error' })
        return
      }

      this.savingBeforeAiAdvert = true
      try {
        // save object and building before generate advert with PIA
        await Promise.all([
          this.saveBuilding(),
          this.saveObject()
        ])
        await this.$refs['object-advertising'].openAdvertGenerationModal()
      } catch (e) {
        console.error(e)
        Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
      } finally {
        this.savingBeforeAiAdvert = false
      }
    },
    editBuildingFields(data) {
      if (data.field === 'constructionYear') {
        this.building.constructionYear = data.value
      } else if (data.field === 'constructionTypes') {
        this.building.constructionTypes = data.value.map(x => x.value)
      } else if (data.field === 'renovationYear') {
        this.building.renovationYear = data.value
      }
    },
    buildingCharacteristicsUpdated(payload) {
      Vue.set(this.building.characteristics, payload.field, payload.value)
    },
    buildingDistanceUpdated(payload) {
      if (this.building.distances == null) {
        Vue.set(this.building, 'distances', {})
      }
      Vue.set(this.building.distances, payload.field, payload.value)
    },
    defaultAnswerUpdated(defaultAnswer) {
      Vue.set(this.object, 'defaultAnswer', defaultAnswer)
    },
    viewingAppointmentUpdated(viewingAppointment) {
      Vue.set(this.object, 'viewingAppointment', viewingAppointment)
    },
    viewingContactUpdated(contactId) {
      if (this.object.contacts) {
        this.object.contacts.contactIdViewingAppointment = contactId
      } else {
        this.object.contacts = {
          contactIdViewingAppointment: contactId
        }
      }
    },
    assigneeMarketingUpdated(contactId) {
      if (this.object.contacts) {
        this.object.contacts.contactIdAssigneeMarketing = contactId
      } else {
        this.object.contacts = {
          contactIdAssigneeMarketing: contactId
        }
      }
    },
    publishAssigneePhoneUpdated(value) {
      if (this.object.contacts) {
        this.object.contacts.publishAssigneePhone = value
      } else {
        this.object.contacts = {
          publishAssigneePhone: value
        }
      }
    },
    emptyDefaultAnswer(empty) {
      this.defaultAnswerNone = empty
    },
    platformsChanged(platforms) {
      const payload = platforms?.platforms || platforms
      this.hasPlatforms = !!(payload.newhome || payload.immoscout || payload.homegate || payload.casaone || payload.flatfox || payload.comparis || payload.properstar || payload.urbanhome || payload.maisonwork || payload.realadvisor || payload.robinreal)
      this.newhomeConfigured = Boolean(platforms?.hasNewHomeExport)
      this.newhomeSelected = !!payload.newhome
      if (!this.newhomeSelected) {
        this.resetNewhomeConfiguration()
      }
    },
    getTodayDateInput() {
      return this.$options.filters.timestampToDateInput(Math.floor(new Date().getTime() / 1000))
    },
    resetNewhomeConfiguration() {
      this.promotionType = ''
      this.promotionPeriod = null
      this.promotionStartDate = this.getTodayDateInput()
      this.promotionStartDateManuallyEdited = false
      this.newhomeFirst = false
      this.newhomeFirstDays = null
    },
    setPromotionType(type) {
      if (!this.isNewhomeConfigurationEditable) {
        return
      }
      this.promotionType = this.promotionType === type ? '' : type
    },
    promotionStartDateChanged(value) {
      this.promotionStartDate = value
      this.promotionStartDateManuallyEdited = true
    },
    dateInputToUnixTimestamp(dateInput) {
      if (!dateInput) {
        return null
      }
      const date = new Date(`${dateInput}T00:00:00`)
      if (Number.isNaN(date.getTime())) {
        return null
      }
      return Math.floor(date.getTime() / 1000)
    },
    unixTimestampToDateInput(timestamp) {
      const parsedTimestamp = Number(timestamp)
      if (!Number.isFinite(parsedTimestamp) || parsedTimestamp <= 0) {
        return null
      }
      const date = new Date(parsedTimestamp * 1000)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    loadSpecialPublication() {
      const specialPublication = this.object?.marketingStatus?.specialPublication
      if (!specialPublication) {
        return
      }
      this.promotionType = this.getPromotionTypeFromApi(specialPublication)
      this.promotionPeriod = specialPublication.promotedDays || null
      if (specialPublication.promotedAt && specialPublication.promotedAt !== '0') {
        this.promotionStartDate = this.unixTimestampToDateInput(specialPublication.promotedAt)
      }
      this.newhomeFirst = !!specialPublication.exclusivity
      this.newhomeFirstDays = specialPublication.exclusiveDays || null
      this.promotionStartDateManuallyEdited = false
    },
    getPromotionTypeFromApi(specialPublication) {
      if (specialPublication.promotionType === 'PROMOTION_TYPE_TOP') {
        return 'top'
      }
      if (specialPublication.promotionType === 'PROMOTION_TYPE_HIGHLIGHT') {
        return 'highlight'
      }
      if (specialPublication.promotionType === 'PROMOTION_TYPE_SPECIAL') {
        return 'special'
      }
      return specialPublication.platform || ''
    },
    getPromotionTypeForApi() {
      if (this.promotionType === 'top') {
        return 'PROMOTION_TYPE_TOP'
      }
      if (this.promotionType === 'highlight') {
        return 'PROMOTION_TYPE_HIGHLIGHT'
      }
      if (this.promotionType === 'special') {
        return 'PROMOTION_TYPE_SPECIAL'
      }
      return 'PROMOTION_TYPE_UNDEFINED'
    },
    updateSpecialPublication() {
      if (!this.object) {
        return
      }
      if (!this.object.marketingStatus) {
        Vue.set(this.object, 'marketingStatus', {})
      }
      const currentMarketingStatus = this.object.marketingStatus
      if (!currentMarketingStatus.specialPublication) {
        Vue.set(currentMarketingStatus, 'specialPublication', {})
      }
      const promotedAt = this.promotionType && this.promotionStartDate
        ? this.dateInputToUnixTimestamp(this.promotionStartDate)?.toString() || '0'
        : '0'
      currentMarketingStatus.specialPublication = {
        promotion: !!this.promotionType,
        platform: this.promotionType ? 'NEWHOME_PLATFORM' : '',
        promotionType: this.getPromotionTypeForApi(),
        promotedAt: promotedAt,
        promotedDays: this.promotionPeriod || 0,
        promotedUntil: '0',
        exclusivity: this.newhomeFirst,
        exclusiveDays: this.newhomeFirst ? this.newhomeFirstDays || 0 : 0,
        exclusiveUntil: '0'
      }
      Vue.set(this.object, 'marketingStatus', currentMarketingStatus)
    },
    getPublishAtTimestamp() {
      if (this.onlineFrom === 'date' && this.onlineFromDate) {
        return Math.floor(new Date(this.onlineFromDate + 'T' + this.time.start + ':00').getTime() / 1000)
      }
      return null
    },
    getPublishUntilTimestamp() {
      if (this.onlineUntil === 'date' && this.unPublishAt) {
        return Math.floor(new Date(this.unPublishAt + 'T' + this.time.end).getTime() / 1000)
      }
      return null
    },
    clickBack() {
      if (this.objectChanged) {
        this.$refs['modal-confirmation'].show()
      } else {
        this.$router.go(-1)
      }
    },
    closeModal() {
      this.$refs['modal-confirmation'].hide()
    },
    discardChanges() {
      this.$router.go(-1)
    },
    async publishData() {
      try {
        await ObjectApi.publishObject(
          [this.id],
          this.getPublishUntilTimestamp(),
          this.unPublishAtAmountOfRequests,
          this.object?.marketingStatus?.specialPublication,
          this.getPublishAtTimestamp()
        )
        await this.$router.push({
          name: 'MarketingObjectDetailsView',
          params: { id: this.id },
          query: { view: 1 }
        })
        Vue.toasted.show(this.$t('message.successMessages.publishObject'), { type: 'success' })
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.savingErrors.publishObject'), { type: 'error' })
        throw e
      }
    },
    async saveData() {
      if (this.$refs['create-building-view'].isInvalid() && this.$refs['create-objects-view'].isInvalid()) {
        this.closeModal()
        await this.saveOnboarding()
        if (this.nextPath !== null) {
          this.nextPath()
        }
      }
    },
    async validateReferenceCheck() {
      const referenceCheckValid = !this.$refs.automaticReferenceCheckCard ||
        !this.$refs.automaticReferenceCheckCard.isInvalid()
      if (!referenceCheckValid) {
        Vue.toasted.show(this.$t('message.savingErrors.mandatoryMissing'), { type: 'error' })
      }
      return referenceCheckValid
    },
    async validateTabOne() {
      return !this.$refs['object-basics'].isInvalid() &&
        !this.$refs['object-features'].isInvalid() &&
        !this.$refs['building-distances'].isInvalid()
    },
    async validateTabTwo() {
      const referenceCheckValid = await this.validateReferenceCheck()
      return !this.$refs['object-advertising'].isInvalid() && referenceCheckValid
    },
    async validateTabThree() {
      return !this.$refs?.editAdvertCommunicationTab?.isInvalid()
    },
    async validateTabFour() {
      this.attemptedSaveTabFour = true
      this.$v.$touch()
      if (this.$v.$invalid) {
        if (this.$v.unPublishAtAmountOfRequests?.$error) {
          Vue.toasted.show(this.$t('message.savingErrors.alreadyReachedAmountRequest'), { type: 'error' })
        }
        if (this.$v.unPublishAt?.$error) {
          if (this.$v.unPublishAt.moreThanToday === false) {
            Vue.toasted.show(this.$t('message.savingErrors.unPublishAtCantBePast'), { type: 'error' })
          }
          if (this.$v.unPublishAt.onlineUntilAfterOnlineFrom === false) {
            Vue.toasted.show(this.$t('message.savingErrors.onlineUntilBeforeOnlineFrom'), { type: 'error' })
          }
        }
        if (this.$v.onlineFromDate?.$error && this.$v.onlineFromDate.moreThanTodayOnlineFrom === false) {
          Vue.toasted.show(this.$t('message.savingErrors.onlineFromCantBePast'), { type: 'error' })
        }
        if (this.$v.promotionStartDate?.$error) {
          // Prioritize one message for the first relevant promotion start validation.
          if (this.$v.promotionStartDate.todayOrFuture === false) {
            Vue.toasted.show(this.$t('message.savingErrors.promotionStartDateCantBePast'), { type: 'error' })
          } else if (this.$v.promotionStartDate.promotionStartAtOrAfterOnlineFrom === false) {
            Vue.toasted.show(this.$t('message.savingErrors.promotionStartBeforeOnlineFrom'), { type: 'error' })
          } else if (this.$v.promotionStartDate.promotionStartAtOrBeforeOnlineUntil === false) {
            Vue.toasted.show(this.$t('message.savingErrors.promotionStartAfterOnlineUntil'), { type: 'error' })
          }
        }
        if (this.$v.promotionPeriod?.$error) {
          if (this.$v.promotionPeriod.promotionPeriodWithinOnlineUntil === false) {
            Vue.toasted.show(this.$t('message.savingErrors.promotionPeriodExtendsBeyondOnlineUntil'), { type: 'error' })
          }
        }
        if (this.hasNewhomeConfigurationErrors) {
          Vue.toasted.show(this.$t('message.savingErrors.mandatoryMissing'), { type: 'error' })
        }
        return false
      }
      return true
    },
    nextClicked(props, canBePublished) {
      if (!props.isLastStep || canBePublished) {
        props.nextTab()
        this.scrollToTop()
      }
    },
    backClicked(props) {
      props.prevTab()
      this.scrollToTop()
    },
    scrollToTop() {
      this.$nextTick(() => {
        const domRect = this.$el.querySelector('#wizard')
        domRect.scrollIntoView(true)
      })
    },
    async save(activeTabIndex, publish = false) {
      let noErrorFlag = true
      if (activeTabIndex === 0) {
        noErrorFlag = await this.validateTabOne()
      } else if (activeTabIndex === 1) {
        noErrorFlag = await this.validateTabTwo()
      } else if (activeTabIndex === 2) {
        noErrorFlag = await this.validateTabThree()
      } else if (activeTabIndex === 3) {
        noErrorFlag = await this.validateTabFour()
      }
      if (noErrorFlag) {
        noErrorFlag = await this.validateReferenceCheck()
      }
      if (noErrorFlag) {
        this.loading = true

        try {
          if (this.canBePublished) {
            await this.$refs['export-platforms']?.savePlatforms()
          }

          await Promise.all([
            this.saveBuilding(),
            this.saveObject(publish)
          ])

          if (!publish) {
            await this.submitPendingPositiveAiFeedback()
          }

          if (publish) {
            await this.publishData()
          } else {
            Vue.toasted.show(this.$t('message.successMessages.changeSaved'), { type: 'success' })
            await this.$router.push({
              name: 'MarketingObjectDetailsView',
              params: { id: this.id },
              query: { view: 1 }
            })
          }
        } catch (e) {
          console.log(e)
        } finally {
          this.loading = false
        }
      }
    },
    async saveBuilding() {
      await ObjectApi.updateBuilding(
        this.building.id,
        this.building.name,
        this.building.objectIds,
        this.building.egid,
        this.building.number,
        this.building.imageMediaId,
        this.building.realEstateNumber,
        this.building.characteristics,
        this.building.constructionYear,
        this.building.renovationYear,
        this.building.buildingZone,
        this.building.plotIds,
        this.building.plotArea,
        this.building.buildingArea,
        this.building.volume,
        this.building.floors,
        this.building.locationQuality,
        this.building.constructionTypes,
        this.building.sia,
        this.building.distances,
        this.building.energySources,
        this.building.documentIds,
        this.building.acquisitionDate,
        this.building.address,
        this.building.note,
        this.building.contractAmendments,
        this.building.rating,
        this.building.buildingStrategy,
        this.building.buildingValue
      )
    },
    async saveObject(publish = false) {
      if (this.object?.viewingAppointment && this.object.viewingAppointment.contactType !== 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
        const newViewingAppointment = this.object.viewingAppointment
        newViewingAppointment.firstName = ''
        newViewingAppointment.lastName = ''
        newViewingAppointment.phone = ''
        newViewingAppointment.email = ''
        Vue.set(this.object, 'viewingAppointment', newViewingAppointment)
      }
      // Remove <p> margins when saving as part of publish
      if (publish && this.object?.defaultAnswer?.text) {
        const normalized = this.removeParagraphMargins(this.object.defaultAnswer.text)
        Vue.set(this.object.defaultAnswer, 'text', normalized)
      }
      this.updateSpecialPublication()
      Vue.set(this.object, 'acceptsCreditTrust', this.acceptsCreditTrust)
      Vue.set(
        this.object,
        'automaticReferenceCheck',
        toObjectApiAutomaticReferenceCheck(
          this.explicitAutomaticReferenceCheckQueryMode,
          this.automaticReferenceCheckInheritsFromAccount
        )
      )
      Vue.set(
        this.object,
        'referenceSenderMailboxId',
        toObjectApiReferenceSenderMailboxId(
          this.explicitReferenceSenderMailboxId,
          this.referenceSenderInheritsFromAccount
        )
      )
      await ObjectApi.updateObject(this.object)
      if (this.object.marketingStatus) {
        await ObjectApi.updateMarketingInfo(
          this.object.id,
          this.object.marketingStatus.marketingNote || '',
          this.object.marketingStatus.startMarketingAt
        )
      }
    }
  },
  validations() {
    let validations = {}
    if (this.onlineUntilValue === 'amount') {
      validations = {
      ...validations,
        unPublishAtAmountOfRequests: {
          required,
          moreThanOverall
        }
      }
    }
    if (this.onlineUntilValue === 'date') {
      validations = {
      ...validations,
        unPublishAt: {
          required,
          moreThanToday,
          onlineUntilAfterOnlineFrom
        }
      }
    }
    validations = {
      ...validations,
      onlineFrom: {
        required
      }
    }
    if (this.onlineFromValue === 'date') {
      validations = {
        ...validations,
        onlineFromDate: {
          required,
          moreThanTodayOnlineFrom
        }
      }
    }
    if (this.isNewhomeConfigurationEditable) {
      if (this.promotionType) {
        validations = {
          ...validations,
          promotionPeriod: {
            required,
            promotionPeriodWithinOnlineUntil
          },
          promotionStartDate: {
            required,
            todayOrFuture,
            promotionStartAtOrAfterOnlineFrom,
            promotionStartAtOrBeforeOnlineUntil
          }
        }
      }
      if (this.newhomeFirst) {
        validations = {
          ...validations,
          newhomeFirstDays: {
            required,
            minValue: minValue(1)
          }
        }
      }
    }
    return validations
  }
}
</script>

<style lang="scss" scoped>
.media-block {
  max-width: 25%;
  margin-right: 0.5%;

  .uploaded-file {
    background-position: center center;
    background-size: cover;
    min-height: 7em;
  }

  .file-block {
    min-height: 7em;
    background: $color-bg-grey-dark;

    a {
      color: $color-text-grey;
      overflow-wrap: break-word;
    }
  }
}

:deep(.global-card) {
  margin-bottom: 0;

  & > .card-body {
    -webkit-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    -moz-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
  }

  &.no-top-border {
    border-top: 0;
  }
}
:deep(.marketing-edit-advert h5.cursor-pointer) {
  font-size: large;
  font-weight: 500 !important;
}

:deep(.vue-form-wizard .wizard-icon-circle .wizard-icon) {
  font-style: initial;
}

:deep(label) {
  width: 100%;
}

.newhome-config-content {
  &.is-disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.segmented-buttons {
  .segmented-button {
    background-color: $color-bg-grey-dark;
    border: 1px solid $color-bg-grey-dark;
    color: $color-text-grey-dark;
    min-width: 95px;

    &.active {
      background-color: $color-primary;
      border-color: $color-primary;
      color: $color-text-white;
    }
  }
}

.readonly-wrapper {

  .inputs-container {
    overflow-y: scroll;
    height: calc(100vh - 100px);
  }

  :deep(.details-row-bg-1) {
    background-color: $color-table-row-2;
  }

  :deep(.details-row-bg-2) {
    background-color: $color-table-row-1;
  }

  :deep(label) {
    width: 100%;
  }

  :deep(.is-disabled) {
    .sort button {
      display: block !important;
    }

    button:not(.sort):not(.page-link) {
      display: none;
    }

    .multiselect, .custom-checkbox {
      pointer-events: none;

      .multiselect__select, .multiselect__placeholder {
        display: none;
      }
    }

    .filter-inputs .multiselect, .custom-checkbox {
      pointer-events: auto !important;

      .multiselect__select, .multiselect__placeholder {
        display: block !important;
      }
    }

    .basic {
      input {
        opacity: 1;
        border: thin solid #ced4da;
        -webkit-box-shadow: inherit;
        box-shadow: inherit;
        margin-bottom: 0;
        pointer-events: initial;
        padding-left: 0.2rem;
        background: none;
      }
    }

    .multi-select-value {
      line-height: 1.5;
    }
  }

  :deep(.collapse-title) {
    &.collapsed {
      h5 {
        background-image: url('../assets/arrow_down.svg');
      }
    }

    h5 {
      background-image: url('../assets/arrow_up.svg');
      background-repeat: no-repeat;
      font-size: large;
      font-weight: 500 !important;
      height: 1.2rem;
      margin-top: 0.2rem;
      margin-bottom: 0.2rem;
      background-position: right;
    }
  }

  :deep(.global-card) {
    margin-bottom: 0;

    & > .card-body {
      -webkit-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
      -moz-box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
      box-shadow: inset 0px -16px 25px -15px rgba(238, 238, 238, 1);
    }

    border-left: 0;
    border-right: 0;
    border-bottom: 0;

    &.no-top-border {
      border-top: 0;
    }
  }

}
</style>
